import React from "react";
import {
  Share2, Database, Clock, ShieldCheck, Lock, Cpu, ListChecks, AlertTriangle, CheckCircle2, Info,
} from "lucide-react";

// ── Score-band styling (mirrors the report's verdict colours) ──────────────────
const BANDS = {
  Excellent: "text-success bg-success/10 border-success/20",
  Good:      "text-primary bg-primary/10 border-primary/20",
  Neutral:   "text-warning bg-warning/10 border-warning/20",
  Bad:       "text-orange-500 bg-orange-500/10 border-orange-500/20",
  Worse:     "text-danger bg-danger/10 border-danger/20",
};

// ── Rubric content (from the AIPolicyScan ranking methodology) ──────────────────
const CATEGORIES = [
  {
    icon: Share2,
    name: "Data Sharing",
    evaluate: [
      "Whether user data is shared with third parties, affiliates, or subprocessors",
      "Purpose of sharing (necessary vs commercial)",
      "Transparency of partner categories",
      "Presence of user control (opt-out / consent)",
    ],
    bands: [
      { range: "9–10", label: "Excellent", points: ["No third-party sharing OR strictly necessary processors only", "Explicit list of subprocessors or categories", "Clear purpose limitation", "User consent required (opt-in)"] },
      { range: "7–8",  label: "Good",      points: ["Limited sharing with defined purposes", "Some transparency, but not exhaustive", "Partial user control"] },
      { range: "5–6",  label: "Neutral",   points: ["Moderate sharing", "Vague partner definitions (“trusted partners”)", "Limited or unclear user control"] },
      { range: "3–4",  label: "Bad",       points: ["Broad sharing including marketing/advertising", "Poor transparency", "Opt-out unclear or difficult"] },
      { range: "1–2",  label: "Worse",     points: ["Extensive sharing or selling of data", "No meaningful disclosure or control"] },
    ],
    penalties: [
      "If data is sold or shared for advertising without explicit opt-in → max score = 3",
      "If no mention of sharing practices → max score = 4",
    ],
  },
  {
    icon: Database,
    name: "Data Usage",
    evaluate: [
      "Purpose limitation",
      "Secondary uses (analytics, ads, profiling)",
      "Use beyond core service delivery",
    ],
    bands: [
      { range: "9–10", label: "Excellent", points: ["Strict purpose limitation", "No secondary usage without explicit opt-in"] },
      { range: "7–8",  label: "Good",      points: ["Limited secondary usage (analytics/improvement)", "Clearly disclosed"] },
      { range: "5–6",  label: "Neutral",   points: ["Broad but disclosed usage", "Includes internal analytics and improvement"] },
      { range: "3–4",  label: "Bad",       points: ["Includes profiling, ads, or behavioral tracking", "Weak purpose limitation"] },
      { range: "1–2",  label: "Worse",     points: ["Unrestricted usage", "No clear purpose boundaries"] },
    ],
    penalties: [
      "If data used for advertising/profiling by default → max score = 4",
      "If purpose not defined → max score = 3",
    ],
  },
  {
    icon: Clock,
    name: "Data Retention & Deletion",
    evaluate: [
      "Retention duration clarity",
      "Criteria for retention",
      "User deletion rights",
      "Completeness of deletion (including backups)",
    ],
    bands: [
      { range: "9–10", label: "Excellent", points: ["Specific retention timelines", "User can fully delete data", "Deletion includes backups and subprocessors"] },
      { range: "7–8",  label: "Good",      points: ["Clear retention logic but not exact durations", "Deletion available but partial limitations"] },
      { range: "5–6",  label: "Neutral",   points: ["General statements (“as long as necessary”)", "Deletion unclear or incomplete"] },
      { range: "3–4",  label: "Bad",       points: ["Retention vague", "Limited or difficult deletion"] },
      { range: "1–2",  label: "Worse",     points: ["No deletion rights", "Indefinite retention"] },
    ],
    penalties: [
      "If no deletion mechanism → max score = 2",
      "If retention undefined → max score = 4",
    ],
  },
  {
    icon: ShieldCheck,
    name: "Security Commitments",
    evaluate: [
      "Mention of safeguards (encryption, access control)",
      "Certifications (ISO 27001, SOC 2, etc.)",
      "Incident response disclosures",
    ],
    bands: [
      { range: "9–10", label: "Excellent", points: ["Strong technical + organizational controls", "Certifications explicitly mentioned", "Breach notification commitments"] },
      { range: "7–8",  label: "Good",      points: ["Good controls but no certifications"] },
      { range: "5–6",  label: "Neutral",   points: ["Generic statements (“we use reasonable security”)"] },
      { range: "3–4",  label: "Bad",       points: ["Minimal security mention"] },
      { range: "1–2",  label: "Worse",     points: ["No security commitments"] },
    ],
    penalties: [
      "If only vague “reasonable measures” → max score = 5",
      "If no mention of security → score = 1",
    ],
  },
  {
    icon: Lock,
    name: "Confidentiality Protections",
    evaluate: [
      "Whether user data is explicitly treated as confidential",
      "Restrictions on internal access/use",
      "Legal framing of confidentiality obligations",
    ],
    bands: [
      { range: "9–10", label: "Excellent", points: ["Explicit confidentiality commitment", "Strict internal access controls"] },
      { range: "7–8",  label: "Good",      points: ["Implied confidentiality but not strongly defined"] },
      { range: "5–6",  label: "Neutral",   points: ["Weak or indirect references"] },
      { range: "3–4",  label: "Bad",       points: ["Minimal mention"] },
      { range: "1–2",  label: "Worse",     points: ["No confidentiality framing"] },
    ],
    penalties: [
      "If no mention of confidentiality → max score = 3",
    ],
  },
  {
    icon: Cpu,
    name: "AI Training & Model Rights",
    evaluate: [
      "(a) Whether user data is used for AI training",
      "(b) Consent model (opt-in vs opt-out vs default-on)",
      "(c) Whether opt-out is available to ALL users (not tier-restricted)",
      "(d) Whether content vs metadata distinction is clearly defined and honored",
    ],
    bands: [
      { range: "9–10", label: "Excellent", points: ["No AI training on user content OR strictly opt-in", "Clear separation of content vs metadata", "Opt-out available universally"] },
      { range: "7–8",  label: "Good",      points: ["Training exists but is opt-in OR strong opt-out", "Clear definitions"] },
      { range: "5–6",  label: "Neutral",   points: ["Training exists with opt-out", "Some ambiguity in definitions"] },
      { range: "3–4",  label: "Bad",       points: ["Training enabled by default (opt-out)", "Limited clarity OR restricted opt-out"] },
      { range: "1–2",  label: "Worse",     points: ["Default-on training", "No opt-out OR unclear usage"] },
    ],
    penalties: [
      "CRITICAL HARD CAP: If user content is used for AI training by default AND no universal opt-out exists (all users cannot opt out) → max score = 3 (regardless of other positives)",
      "If content vs metadata distinction is missing → max score = 5",
      "If training terms are vague/ambiguous → max score = 4",
    ],
  },
];

const INDICATOR_MAP = [
  { range: "9–10", label: "Excellent" },
  { range: "7–8",  label: "Good" },
  { range: "5–6",  label: "Neutral" },
  { range: "3–4",  label: "Bad" },
  { range: "1–2",  label: "Worse" },
];

const SAFEGUARDS = [
  "Do NOT infer beyond text evidence",
  "If information is missing → apply penalties explicitly",
  "Prefer the lower score when ambiguity exists",
  "Apply hard caps before final scoring",
  "Avoid reinterpreting the rubric across runs",
];

const Bullet = ({ children }) => (
  <li className="flex gap-2 text-sm text-brand-muted leading-relaxed">
    <span className="mt-2 w-1.5 h-1.5 rounded-full bg-primary/40 flex-shrink-0" aria-hidden />
    <span>{children}</span>
  </li>
);

const ScoringGuidePage = () => (
  <div className="flex flex-col gap-4 max-w-5xl">
    {/* Page header */}
    <div>
      <div className="flex items-center gap-2">
        <ListChecks className="w-6 h-6 text-primary" />
        <h1 className="text-2xl font-extrabold text-brand-text">Scoring Guide</h1>
      </div>
      <p className="text-sm text-brand-muted mt-1">
        How AIPolicyScan rates each privacy policy. Every category is scored 1–10 against the rules below,
        with hard caps applied before the final score.
      </p>
    </div>

    {/* Categories */}
    {CATEGORIES.map(({ icon: Icon, name, evaluate, bands, penalties }, idx) => (
      <section key={name} className="bg-white border border-surface-border rounded-2xl overflow-hidden">
        {/* Category header */}
        <div className="flex items-center gap-3 px-5 sm:px-6 py-4 border-b border-surface-border">
          <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Icon className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h2 className="text-base font-bold text-brand-text leading-tight">
              <span className="text-primary">{idx + 1}.</span> {name}
            </h2>
            <p className="text-xs text-brand-light">Score: 1–10</p>
          </div>
        </div>

        <div className="px-5 sm:px-6 py-5 space-y-5">
          {/* Evaluate */}
          <div>
            <h3 className="text-xs font-bold uppercase tracking-wide text-brand-muted mb-2">Evaluate</h3>
            <ul className="space-y-1.5">
              {evaluate.map((e) => <Bullet key={e}>{e}</Bullet>)}
            </ul>
          </div>

          {/* Scoring bands */}
          <div>
            <h3 className="text-xs font-bold uppercase tracking-wide text-brand-muted mb-2">Scoring rules</h3>
            <div className="space-y-2.5">
              {bands.map(({ range, label, points }) => (
                <div key={range} className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                  <span className={`inline-flex items-center self-start gap-1.5 px-2.5 py-1 rounded-full border text-xs font-semibold whitespace-nowrap ${BANDS[label]}`}>
                    {range} · {label}
                  </span>
                  <ul className="flex-1 space-y-1 pt-0.5">
                    {points.map((p) => <Bullet key={p}>{p}</Bullet>)}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          {/* Penalties */}
          <div className="rounded-xl border border-danger/20 bg-danger/5 px-4 py-3">
            <h3 className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-danger mb-2">
              <AlertTriangle className="w-3.5 h-3.5" /> Hard penalties
            </h3>
            <ul className="space-y-1.5">
              {penalties.map((p) => (
                <li key={p} className="text-sm text-brand-muted leading-relaxed flex gap-2">
                  <span className="mt-2 w-1.5 h-1.5 rounded-full bg-danger/50 flex-shrink-0" aria-hidden />
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>
    ))}

    {/* Output rules + safeguards */}
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <section className="bg-white border border-surface-border rounded-2xl p-5 sm:p-6">
        <h2 className="flex items-center gap-2 text-base font-bold text-brand-text mb-3">
          <CheckCircle2 className="w-5 h-5 text-success" /> Final scoring output
        </h2>
        <p className="text-sm text-brand-muted mb-3">For each category the analyzer returns:</p>
        <ul className="space-y-1.5 mb-4">
          <Bullet><span className="font-semibold text-brand-text">score</span> (1–10)</Bullet>
          <Bullet><span className="font-semibold text-brand-text">indicator</span> (mapped from the score)</Bullet>
          <Bullet><span className="font-semibold text-brand-text">summary</span></Bullet>
          <Bullet><span className="font-semibold text-brand-text">confidence_score</span></Bullet>
        </ul>
        <div className="flex flex-wrap gap-2">
          {INDICATOR_MAP.map(({ range, label }) => (
            <span key={range} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-semibold ${BANDS[label]}`}>
              {range} · {label}
            </span>
          ))}
        </div>
      </section>

      <section className="bg-white border border-surface-border rounded-2xl p-5 sm:p-6">
        <h2 className="flex items-center gap-2 text-base font-bold text-brand-text mb-3">
          <Info className="w-5 h-5 text-primary" /> Determinism safeguards
        </h2>
        <ul className="space-y-1.5">
          {SAFEGUARDS.map((s) => <Bullet key={s}>{s}</Bullet>)}
        </ul>
      </section>
    </div>
  </div>
);

export default ScoringGuidePage;
